// MQTT-over-WSS client wrapper around mqtt.js. Handles:
//   - Cognito credential fetch
//   - SigV4 URL signing
//   - Connection lifecycle (connect, reconnect, presence)
//   - Topic subscribe + publish with envelope wrapping
//   - Bridges incoming messages to an EventBus

import mqtt from 'mqtt';
import { presignIotWsUrl } from './sigv4.js';
import { getUnauthCredentials, getAuthCredentials } from './credentials.js';
import { attachIotPolicyOnce } from './iot-attach.js';
import { envelope, isValid, topicFor, topics, EventType } from './protocol.js';

export class IotClient {
  /**
   * @param {object} opts
   * @param {object} opts.config       Loaded /config.json
   * @param {string} opts.session      Session ID
   * @param {string} opts.from         'speaker' | 'viewer' | 'admin'
   * @param {EventBus} opts.bus        Inbound messages emit on this bus
   * @param {string=} opts.idToken     User Pool ID token if authenticated (admin)
   */
  constructor({ config, session, from, bus, idToken }) {
    this.config  = config;
    this.session = session;
    this.from    = from;
    this.bus     = bus;
    this.idToken = idToken;

    this.client     = null;
    this.clientId   = `${from}-${session}-${Math.random().toString(36).slice(2, 10)}`;
    this.connected  = false;
    this.subscribedTopics = new Set();
    this.heartbeatTimer   = null;
    this.lastError  = null;

    this.onConnect    = () => {};
    this.onDisconnect = () => {};
    this.onError      = () => {};
  }

  async connect() {
    const { region, iotEndpoint, identityPoolId, userPoolId } = this.config;

    console.log('[iot] fetching credentials, mode=', this.idToken ? 'AUTH' : 'UNAUTH');
    let creds;
    try {
      creds = this.idToken
        ? await getAuthCredentials({ region, identityPoolId, userPoolId, idToken: this.idToken })
        : await getUnauthCredentials({ region, identityPoolId });
      console.log('[iot] got creds, identityId=', creds.identityId);
    } catch (e) {
      console.error('[iot] credential fetch failed:', e);
      this.lastError = 'creds: ' + (e.message || e);
      this.onError(e);
      this.onDisconnect();
      throw e;
    }

    // Authenticated identities require an attached IoT policy (see comment in
    // CDK stack). Idempotent + memoized in sessionStorage; runs once per tab.
    if (this.idToken && this.config.authIotPolicy) {
      try {
        await attachIotPolicyOnce({
          region,
          identityId: creds.identityId,
          policyName: this.config.authIotPolicy,
          creds,
        });
        console.log('[iot] policy attached:', this.config.authIotPolicy);
      } catch (e) {
        console.warn('[iot] AttachPolicy failed (will try connect anyway):', e.message);
      }
    }

    const url = await presignIotWsUrl({ endpoint: iotEndpoint, region, creds });
    console.log('[iot] connecting to', iotEndpoint, 'as', this.clientId);

    this.client = mqtt.connect(url, {
      clientId:        this.clientId,
      protocolVersion: 5,                     // MQTT 5
      keepalive:       30,
      reconnectPeriod: 2000,                  // start at 2s, mqtt.js handles backoff
      connectTimeout:  10_000,
      clean:           true,
    });

    this.client.on('connect', (packet) => {
      console.log('[iot] connected', packet?.reasonCode);
      this.lastError = null;
      this.connected = true;
      this.publishPresence(EventType.PRESENCE_ONLINE);
      this.startHeartbeat();
      // Re-subscribe on reconnect.
      for (const t of this.subscribedTopics) this.client.subscribe(t, { qos: 0 });
      this.onConnect();
    });

    this.client.on('reconnect', () => { console.log('[iot] reconnecting…'); this.connected = false; });
    this.client.on('close',     () => { console.log('[iot] socket closed'); this.connected = false; this.stopHeartbeat(); this.onDisconnect(); });
    this.client.on('error',     (e) => { console.error('[iot] error:', e); this.lastError = e.message || String(e); this.onError(e); });

    this.client.on('message', (topic, payloadBuf) => {
      let msg;
      try { msg = JSON.parse(payloadBuf.toString()); }
      catch { return; /* malformed; drop */ }
      if (!isValid(msg))            return;
      if (msg.session !== this.session) return;
      this.bus.emit(msg.type, { ...msg.payload, _meta: { from: msg.from, ts: msg.ts } });
    });
  }

  subscribe(topic) {
    if (this.subscribedTopics.has(topic)) return;
    this.subscribedTopics.add(topic);
    if (this.connected) this.client.subscribe(topic, { qos: 0 });
  }

  publish(type, payload) {
    if (!this.connected) return false;
    const topic = topicFor(type, this.session);
    const msg   = envelope({ type, session: this.session, from: this.from, payload });
    this.client.publish(topic, JSON.stringify(msg), { qos: 0 });
    return true;
  }

  publishPresence(state) {
    this.publish(state, { state: state.split('.')[1], clientId: this.clientId, ua: navigator.userAgent });
  }

  startHeartbeat() {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => this.publishPresence(EventType.PRESENCE_HEARTBEAT), 5_000);
  }
  stopHeartbeat() {
    if (this.heartbeatTimer) { clearInterval(this.heartbeatTimer); this.heartbeatTimer = null; }
  }

  async disconnect() {
    if (!this.client) return;
    this.publishPresence(EventType.PRESENCE_OFFLINE);
    this.stopHeartbeat();
    await new Promise(r => this.client.end(false, {}, r));
    this.connected = false;
  }
}

export { topics, EventType };
